import type { AssetOperationAvailabilityItem } from '@fleet-bffs/api-contract';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as roboticMowerUpdateActions from '~/ducks/asset-updates/actions';

interface HfsActionsAvailabilityRecord {
  iprId: string;
  hfsActions: AssetOperationAvailabilityItem[];
}

interface AssetHfsActionsUpdateEventTimestamp {
  iprId: string;
  timestamp: string;
}

export const initialState: Fields = {
  items: [],
  updateEventsTimestamps: [],
};

export interface Fields {
  items: HfsActionsAvailabilityRecord[];
  updateEventsTimestamps: AssetHfsActionsUpdateEventTimestamp[];
}

const reducer = createSlice({
  name: 'assets-hfs-actions',
  initialState,
  reducers: {
    setAssetsActions: (
      state,
      action: PayloadAction<{ actions: AssetOperationAvailabilityItem[]; iprIds: string[] }>
    ) => {
      const replaced = new Set(action.payload.iprIds);
      const actionsByIprId = new Map<string, AssetOperationAvailabilityItem[]>();
      action.payload.actions.forEach((hfsAction) => {
        const list = actionsByIprId.get(hfsAction.iprId);
        if (list) {
          list.push(hfsAction);
        } else {
          actionsByIprId.set(hfsAction.iprId, [hfsAction]);
        }
      });
      state.items = [
        ...state.items.filter((record) => !replaced.has(record.iprId)),
        ...action.payload.iprIds.map((iprId) => ({
          iprId,
          hfsActions: actionsByIprId.get(iprId) ?? [],
        })),
      ].sort((a, b) => a.iprId.localeCompare(b.iprId));
    },
    registerHfsActionsUpdateEvent: (
      state,
      action: PayloadAction<{ timestamp: string; iprId: string }>
    ) => {
      state.updateEventsTimestamps = [
        ...state.updateEventsTimestamps.filter((record) => action.payload.iprId != record.iprId),
        action.payload,
      ];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      roboticMowerUpdateActions.updateAssetHfsActions.type,
      (state, action: ReturnType<typeof roboticMowerUpdateActions.updateAssetHfsActions>) => {
        state.items = [
          ...state.items.filter((record) => record.iprId !== action.payload.assetId),
          {
            iprId: action.payload.assetId,
            hfsActions: action.payload.updated,
          },
        ].sort((a, b) => a.iprId.localeCompare(b.iprId));
      }
    );
  },
});

export const { setAssetsActions, registerHfsActionsUpdateEvent } = reducer.actions;

export default reducer.reducer;
